import type {
  AllComponents,
  WebpageComponentRelation,
} from "../../../../components/_default/interfaces/base";
import { resolveComponentMapKey } from "./structureIdentity";
import { isChildAllowedUnderParent } from "./dragDropStructureRules";

export type StructureComponentsMap = Record<string, AllComponents>;

export interface StructureReorderPayload {
  parent_id: string | number;
  draggedComponentId: string | number;
  destinationId: string | number;
  position: string;
  section: boolean;
}

export type StructureReorderPlanResult =
  | { ok: false }
  | {
      ok: true;
      sourceParentKey: string;
      targetParentKey: string;
      draggedKey: string;
      destinationKey: string;
      draggedIndex: number;
      insertIndex: number
      sourceRelation: WebpageComponentRelation;
    };

function normalizeDropPosition(
  position: string | null | undefined
): "top" | "bottom" | "middle" {
  if (position === "top" || position === "bottom") {
    return position;
  }
  return "middle";
}

function resolveParentKeyFromComponent(
  components: StructureComponentsMap,
  component: AllComponents | undefined
): string | null {
  if (!component) {
    return null;
  }
  const parentUuid = (component as { parent_uuid?: string | null }).parent_uuid?.trim();
  if (parentUuid) {
    const byUuid = resolveComponentMapKey(components, parentUuid);
    if (byUuid) return byUuid;
  }
  if (component.parent_id != null) {
    const byId = resolveComponentMapKey(components, component.parent_id);
    if (byId) return byId;
  }
  return null;
}

function resolveRelationChildKey(
  components: StructureComponentsMap,
  relation: WebpageComponentRelation | null | undefined
): string | null {
  if (!relation) {
    return null;
  }
  const childUuid = (relation as { child_uuid?: string | null }).child_uuid?.trim();
  if (childUuid) {
    return resolveComponentMapKey(components, childUuid) ?? childUuid;
  }
  if (relation.child_id != null) {
    return (
      resolveComponentMapKey(components, relation.child_id) ?? String(relation.child_id)
    );
  }
  return null;
}

function findRelationIndexByChildKey(
  components: StructureComponentsMap,
  parent: AllComponents | undefined,
  childKey: string
): number {
  if (!parent || !Array.isArray(parent.childWebpageComponentRelations)) {
    return -1;
  }
  return parent.childWebpageComponentRelations.findIndex((relation) => {
    const relationChildKey = resolveRelationChildKey(components, relation);
    return relationChildKey === childKey;
  });
}

function isColumnComponent(component: AllComponents | undefined): boolean {
  if (!component) {
    return false;
  }
  const classes = String(component.attributes?.classes ?? "");
  const tokens = classes.split(/\s+/).filter(Boolean);
  return tokens.some((token) => token === "col" || token.startsWith("col-"));
}

function isRowComponent(
  components: StructureComponentsMap,
  component: AllComponents | undefined
): boolean {
  if (!component) {
    return false;
  }
  const classes = String(component.attributes?.classes ?? "").toLowerCase();
  const normalized = ` ${classes} `;
  if (normalized.includes(" row ") || /\brow-[a-z0-9_-]+\b/.test(classes)) {
    return true;
  }
  if (component.type !== "section") {
    return false;
  }
  const relations = Array.isArray(component.childWebpageComponentRelations)
    ? component.childWebpageComponentRelations
    : [];
  return relations.some((relation) => {
    const childKey = resolveRelationChildKey(components, relation);
    const child = childKey ? components[childKey] : undefined;
    return isColumnComponent(child);
  });
}

/**
 * Pure preview of UPDATE_STRUCTURE_COMPONENT_ORDER: validates reparent rules and
 * resolves insert index before any relation array is mutated.
 */
export function computeStructureReorderPlan(
  components: StructureComponentsMap,
  payload: StructureReorderPayload
): StructureReorderPlanResult {
  const { parent_id, draggedComponentId, destinationId, position, section } = payload;

  const draggedKey =
    resolveComponentMapKey(components, draggedComponentId) ??
    String(draggedComponentId);
  const destinationKey =
    resolveComponentMapKey(components, destinationId) ?? String(destinationId);
  const draggedComponent = components[draggedKey];
  const destinationComponent = components[destinationKey];

  if (!draggedComponent || !destinationComponent) {
    return { ok: false };
  }
  if (draggedKey === destinationKey) {
    return { ok: false };
  }

  const fallbackParentKey =
    resolveComponentMapKey(components, parent_id) ?? String(parent_id);
  const sourceParentKey =
    resolveParentKeyFromComponent(components, draggedComponent) ?? fallbackParentKey;
  const sourceParent = components[sourceParentKey];
  if (!sourceParent || !Array.isArray(sourceParent.childWebpageComponentRelations)) {
    return { ok: false };
  }

  const draggedIndex = findRelationIndexByChildKey(
    components,
    sourceParent,
    draggedKey
  );
  if (draggedIndex < 0) {
    return { ok: false };
  }

  const sourceRelation = sourceParent.childWebpageComponentRelations[draggedIndex];
  if (!sourceRelation) {
    return { ok: false };
  }

  const destinationOwnParentKey = resolveParentKeyFromComponent(
    components,
    destinationComponent
  );
  let targetParentKey: string | null = null;
  const draggedIsColumn = isColumnComponent(draggedComponent);
  const destinationIsColumn = isColumnComponent(destinationComponent);
  const draggedIsRow = isRowComponent(components, draggedComponent);
  const destinationIsRow = isRowComponent(components, destinationComponent);

  if (draggedIsColumn) {
    if (!destinationIsColumn) {
      return { ok: false };
    }
    targetParentKey = destinationOwnParentKey ?? sourceParentKey;
  } else if (draggedIsRow) {
    if (!destinationIsRow) {
      return { ok: false };
    }
    targetParentKey = destinationOwnParentKey ?? sourceParentKey;
  } else if (
    destinationComponent.type === "section" ||
    destinationComponent.type === "inner_section"
  ) {
    targetParentKey = destinationKey;
  } else if (destinationOwnParentKey) {
    targetParentKey = destinationOwnParentKey;
  } else if (section) {
    targetParentKey = destinationKey;
  }

  if (!targetParentKey) {
    return { ok: false };
  }

  const targetParent = components[targetParentKey];
  if (!targetParent || !Array.isArray(targetParent.childWebpageComponentRelations)) {
    return { ok: false };
  }

  if (!isChildAllowedUnderParent(targetParent.type, draggedComponent.type)) {
    return { ok: false };
  }

  // Content widgets must live under a column parent (nav items stay under nav / navigation-container).
  const layoutDragTypes = new Set(["section", "inner_section"]);
  const needsColumnParent =
    !draggedIsColumn &&
    !draggedIsRow &&
    !layoutDragTypes.has(String(draggedComponent.type ?? ""));
  if (needsColumnParent) {
    const navItemUnderNavTree =
      draggedComponent.type === "nav-item" &&
      (targetParent.type === "nav" || targetParent.type === "navigation-container");
    if (!navItemUnderNavTree && !isColumnComponent(targetParent)) {
      return { ok: false };
    }
  }

  const dropPosition = normalizeDropPosition(position);
  const destinationIndexInTarget = findRelationIndexByChildKey(
    components,
    targetParent,
    destinationKey
  );
  const movingWithinSameParent = sourceParentKey === targetParentKey;

  let insertIndex: number;

  if (movingWithinSameParent) {
    const rels = sourceParent.childWebpageComponentRelations;
    const keys = rels.map(
      (relation) => resolveRelationChildKey(components, relation) ?? ""
    );
    keys.splice(draggedIndex, 1);
    const destAfter = keys.indexOf(destinationKey);
    if (destAfter < 0) {
      return { ok: false };
    }
    insertIndex = dropPosition === "top" ? destAfter : destAfter + 1;
  } else {
    insertIndex = targetParent.childWebpageComponentRelations.length;
    if (destinationIndexInTarget >= 0) {
      if (dropPosition === "top") {
        insertIndex = destinationIndexInTarget;
      } else if (dropPosition === "bottom") {
        insertIndex = destinationIndexInTarget + 1;
      } else if (
        destinationComponent.type === "section" ||
        destinationComponent.type === "inner_section"
      ) {
        insertIndex = targetParent.childWebpageComponentRelations.length;
      } else {
        insertIndex = destinationIndexInTarget + 1;
      }
    } else if (destinationKey !== targetParentKey) {
      return { ok: false };
    }
  }

  return {
    ok: true,
    sourceParentKey,
    targetParentKey,
    draggedKey,
    destinationKey,
    draggedIndex,
    insertIndex,
    sourceRelation,
  };
}

/**
 * Returns whether the current structure would accept this reorder (for DnD UI gating).
 */
export function isStructureReorderAllowed(
  components: StructureComponentsMap,
  payload: StructureReorderPayload
): boolean {
  return computeStructureReorderPlan(components, payload).ok;
}

/** Passed from Editor into drag-over preview so highlights match {@link updateComponentStructureOrder} gating. */
export type BuilderDnDReorderGate = {
  isAllowed: (payload: StructureReorderPayload) => boolean;
  genericOrderParent: string | number;
  structureOrderParent: string | number;
};
